/**
 * MyTasksNewTaskForm
 * Simplified inline add-task form for the My Tasks page.
 * Single-row layout: task name | workflow instance | due date | Add / Cancel
 * Slides in with a CSS animation when rendered.
 */

import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuthStore } from '../../stores/authStore';
import { useTaskUIStore } from '../../stores/taskUIStore';
import { useCreateTask } from '../../hooks/useTasks';
import { useClientList } from '../../hooks/useClients';

// ============================================================================
// Animation
// ============================================================================

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============================================================================
// Styled components
// ============================================================================

const FormCard = styled.section`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  animation: ${slideDown} ${({ theme }) => theme.transitions.base};
`;

const FormTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TextInput = styled.input`
  flex: 2;
  min-width: 160px;
  height: 38px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: inherit;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const Select = styled.select`
  flex: 2;
  min-width: 160px;
  height: 38px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: inherit;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const DateInput = styled.input`
  flex: 1;
  min-width: 140px;
  height: 38px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: inherit;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const AddButton = styled.button`
  height: 36px;
  padding: 0 ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.gradients.button};
  color: ${({ theme }) => theme.colors.text.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const CancelButton = styled.button`
  height: 36px;
  padding: 0 ${({ theme }) => theme.spacing[4]};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: inherit;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borders.dark};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ErrorText = styled.p`
  margin: ${({ theme }) => theme.spacing[2]} 0 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.error.text};
`;

// ============================================================================
// Types
// ============================================================================

interface MyTasksNewTaskFormProps {
  /** Workflow options derived from the parent page's task list — avoids a duplicate fetch. */
  workflowOptions: Array<{ value: string; label: string }>;
}

// ============================================================================
// Component
// ============================================================================

export function MyTasksNewTaskForm({ workflowOptions }: MyTasksNewTaskFormProps) {
  const { toggleMyTasksForm } = useTaskUIStore();
  const { user } = useAuthStore();
  const createTask = useCreateTask();

  // A new task is new clinical work: Discharged and Inactive clients aren't offered
  // (ADO #585). The rule lives on the server, so this form doesn't hard-code statuses.
  const { data: clientsData } = useClientList({ limit: 100, workflowEligible: true });
  const clients = clientsData?.data ?? [];

  const [taskName, setTaskName] = useState('');
  const [workflowInstanceId, setWorkflowInstanceId] = useState('');
  const [clientMemberId, setClientMemberId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!taskName.trim()) {
      setError('Task name is required.');
      return;
    }
    if (!user?.id) {
      setError('User session not found. Please refresh and try again.');
      return;
    }

    setError(null);

    try {
      await createTask.mutateAsync({
        name: taskName.trim(),
        type: 'AD_HOC',
        assignedToUserId: user.id,
        scheduledDate: dueDate || new Date().toISOString().slice(0, 10),
        ...(dueDate ? { dueDate } : {}),
        ...(workflowInstanceId ? { workflowInstanceId } : {}),
        ...(clientMemberId ? { clientMemberId } : {}),
      });
      toggleMyTasksForm();
    } catch {
      setError('Failed to create task. Please try again.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      toggleMyTasksForm();
    }
  };

  return (
    <FormCard aria-label="Add new task form" onKeyDown={handleKeyDown}>
      <FormTitle>Add Task</FormTitle>

      <FieldRow>
        <TextInput
          type="text"
          placeholder="Task name..."
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          aria-label="Task name"
          autoFocus
        />

        <Select
          value={workflowInstanceId}
          onChange={(e) => setWorkflowInstanceId(e.target.value)}
          aria-label="Link to workflow instance (optional)"
        >
          <option value="">No workflow</option>
          {workflowOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>

        <Select
          value={clientMemberId}
          onChange={(e) => setClientMemberId(e.target.value)}
          aria-label="Link to client (optional)"
        >
          <option value="">No client</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.firstName} {c.lastName}
            </option>
          ))}
        </Select>

        <DateInput
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          aria-label="Due date"
        />
      </FieldRow>

      {error && <ErrorText role="alert">{error}</ErrorText>}

      <ButtonRow>
        <AddButton
          type="button"
          onClick={handleSubmit}
          disabled={createTask.isPending}
          aria-busy={createTask.isPending}
        >
          {createTask.isPending ? 'Adding...' : 'Add'}
        </AddButton>
        <CancelButton type="button" onClick={toggleMyTasksForm}>
          Cancel
        </CancelButton>
      </ButtonRow>
    </FormCard>
  );
}

export default MyTasksNewTaskForm;
